import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseRounded, LockRounded, VisibilityRounded } from '@mui/icons-material';
import { SurveyQuestionInput } from '../../portal-v2/components/SurveyQuestionInput';
import type { SurveyAnswerValue, SurveyTemplateDto } from '../../../lib/api/survey.api';

interface Props {
  template: SurveyTemplateDto;
  /** Title the staff typed; falls back to the template name. */
  title: string;
  onClose: () => void;
}

/** Read-through preview of the survey exactly as the student will see it.
 *  Inputs are interactive (local, ephemeral state) but nothing is submitted. */
export function SurveyPreviewDialog({ template, title, onClose }: Props) {
  const { t } = useTranslation('sis');
  const [answers, setAnswers] = useState<Record<string, SurveyAnswerValue>>({});

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <VisibilityRounded fontSize="small" color="action" />
          <span>{t('surveys.previewTitle')}</span>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6" fontWeight={700}>
          {title.trim() || template.name}
        </Typography>
        <Chip
          size="small"
          icon={<LockRounded />}
          label={t('portal.surveys.anonymousNote')}
          color="success"
          variant="outlined"
          sx={{ mt: 1 }}
        />
        {template.welcomeMessage && (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            {template.welcomeMessage}
          </Typography>
        )}

        <Box sx={{ mt: 3 }}>
          <Stack spacing={3} divider={<Divider flexItem />}>
            {template.questions.map((q) => (
              <SurveyQuestionInput
                key={q.id}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
              />
            ))}
          </Stack>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          {t('surveys.previewNote')}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
